import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useForm, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { authMessages } from '@coligo-org/fe-common/translations';
import constants, { STORAGE_KEYS } from '@coligo-org/fe-common/constants';

import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Visibility,
  VisibilityOff,
  HighlightOff as ErrorIcon,
} from '@mui/icons-material';
import {
  Grid,
  Box,
  FormControl,
  Typography,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import useRoles from 'hooks/useRoles';
import getCustomSchoolData from 'custom/schools';
import logService from 'common/services/logService';
import { getSocket } from '../../../app/helpers/socketManager/socket';
import LoadableButton from '../../../common/components/LoadableButton';
import styles from './styles';
import { signIn } from '../../state/actions/auth';
import getDefaultRoute from '../../../app/helpers/getDefaultRoute';
import { getCurrentUser, setJwt } from '../../services/authService';
import Request from '../ForgotPassword/Request';
import ChooseAccount from '../ForgotPassword/ChooseAccount';
import Pending from '../ForgotPassword/Pending';
import validate from './validate';
import loginImage from '../../../common/assets/img/login.png';
import QRCode from './QRCode';

const expiredJWTQueryParam = 'expired';
const deactivatedUserQueryParam = 'deactivated';

const customSchoolData = getCustomSchoolData();

function LoginForm({ classes, login, errorMessage, isAuthenticatingUser }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showQrLogin, setShowQrLogin] = useState(false);
  const [isCapsLockEnabled, setIsCapsLockEnabled] = useState(false);
  const [isDeactivatedUser, setIsDeactivatedUser] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [forgetPassword, setForgetPassword] = useState(false);
  const [showChooseAccount, setShowChooseAccount] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [chosenAccount, setChosenAccount] = useState();
  const [socketId, setSocketId] = useState('');

  const { allRoles } = useRoles();

  const queryParams = new URLSearchParams(search);
  const validationSchema = validate(intl);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const onSubmit = async data => {
    if (queryParams.has(expiredJWTQueryParam)) {
      queryParams.delete(expiredJWTQueryParam);
      navigate('', { replace: true, search: queryParams.toString() });
    }
    if (queryParams.has(deactivatedUserQueryParam)) {
      queryParams.delete(deactivatedUserQueryParam);
      navigate('', { replace: true, search: queryParams.toString() });
    }
    const newProps = { ...data, username: data.username.trim() };
    await login(
      newProps,
      defaultRoute => navigate(defaultRoute, { replace: true }),
      () => setIsDeactivatedUser(true),
      allRoles,
    );
  };

  const handleCapsLock = event => {
    if (event && event.getModifierState && event.getModifierState('CapsLock')) {
      setIsCapsLockEnabled(true);
    } else {
      setIsCapsLockEnabled(false);
    }
  };

  const handleTokenScanned = useCallback(
    async data => {
      const {
        TOKEN_KEY,
        REFRESH_TOKEN_KEY,
        REFRESH_TOKEN_ID_KEY,
        STRINGIFIED_USER_DATA_KEY,
      } = STORAGE_KEYS;
      logService.info('token scanned data', data);
      localStorage.setItem(TOKEN_KEY, data.jwt);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
      localStorage.setItem(REFRESH_TOKEN_ID_KEY, data.refreshTokenId);
      localStorage.setItem(
        STRINGIFIED_USER_DATA_KEY,
        JSON.stringify(data.user),
      );
      setJwt(data.jwt);
      await login(
        {},
        defaultRoute => navigate(defaultRoute, { replace: true }),
        () => setIsDeactivatedUser(true),
        allRoles,
      );
    },
    [navigate, allRoles, login],
  );

  const handleRoomJoined = useCallback(
    roomId => {
      logService.info('ROOM JOINED', roomId);
      const key = roomId.split(':')[1];
      setSocketId(key);
    },
    [setSocketId],
  );

  useEffect(() => {
    const user = getCurrentUser();
    if (Object.keys(user).length) {
      navigate(getDefaultRoute(user, allRoles), { replace: true });
    }
  }, [allRoles, navigate]);

  useEffect(() => {
    const socket = getSocket();
    if (constants?.SOCKETS && socket) {
      const { SOCKET_GLOBAL_EVENT_JOIN_ROOM, SOCKET_GLOBAL_EVENT_ROOM_JOINED } =
        constants.SOCKETS.GLOBAL_EVENTS;

      socket.on('connect', () => {
        logService.info('SOCKET CONNECTED');

        socket.emit(SOCKET_GLOBAL_EVENT_JOIN_ROOM, {
          moduleName: '@module/authentication',
          roomType: '@room-types/auth/qr-tokens',
        });
        socket.on(SOCKET_GLOBAL_EVENT_ROOM_JOINED, handleRoomJoined);
        socket.on('@socket-events/auth/token/scanned', handleTokenScanned);
      });
    }
    return () => {
      if (socket) {
        socket.off('connect');
        socket.off(
          constants.SOCKETS.GLOBAL_EVENTS.SOCKET_GLOBAL_EVENT_ROOM_JOINED,
        );
        socket.off('@socket-events/auth/token/scanned');
      }
    };
  }, [handleRoomJoined, handleTokenScanned]);

  const expiryStatus = queryParams.get(expiredJWTQueryParam);
  const deactivationStatus = queryParams.get(deactivatedUserQueryParam);
  const showWarnings = {
    1: true,
  };

  return (
    <Grid container className={classes.mainGrid}>
      {isMdUp && (
        <Grid item md={6} className={classes.imageContainer}>
          <Box>
            <Typography className={classes.logoTextDecoration}>
              Coligo
            </Typography>
            <Typography className={classes.descriptionTextDecoration}>
              Smarter Starts here !
            </Typography>
          </Box>
          <img src={loginImage} alt="login" />
        </Grid>
      )}
      <Grid item xs={12} md={6} className={classes.formContainer}>
        <Box className={classes.centerItem}>
          {customSchoolData && (
            <img
              className={classes.logoImage}
              src={customSchoolData?.logo}
              alt={customSchoolData?.name}
            />
          )}
        </Box>

        <Box className={classes.mainContentWrapper}>
          {showLogin && (
            <>
              <Box className={classes.titleBox}>
                <Typography
                  fontWeight={600}
                  fontSize="2.5em"
                  className={classes.descriptionColor}
                >
                  Log In to Coligo LMS
                </Typography>
                <Typography
                  fontWeight={500}
                  className={classes.descriptionColor}
                >
                  Easy management for your school profiles with coligo.
                </Typography>
              </Box>
              {showWarnings[expiryStatus] && (
                <Grid align="center" className={classes.warning}>
                  <ErrorIcon />
                  <Typography variant="subtitle1">
                    <FormattedMessage {...authMessages.expirationWarning} />
                  </Typography>
                </Grid>
              )}
              {(showWarnings[deactivationStatus] || isDeactivatedUser) && (
                <Grid align="center" className={classes.warning}>
                  <ErrorIcon />
                  <Typography variant="subtitle1">
                    <FormattedMessage {...authMessages.deactivationWarning} />
                  </Typography>
                </Grid>
              )}
              <form
                className={classes.form}
                onSubmit={handleSubmit(onSubmit)}
                data-testid="loginForm"
              >
                <FormControl variant="outlined" margin="normal" fullWidth>
                  <Controller
                    name="username"
                    control={control}
                    render={({ field: { onChange, ...field } }) => (
                      <>
                        <label
                          className={classes.inputsLabel}
                          htmlFor="username-input"
                        >
                          {intl.formatMessage({
                            ...authMessages.userNameLabel,
                          })}
                        </label>
                        <TextField
                          id="username-input"
                          variant="outlined"
                          placeholder={intl.formatMessage({
                            ...authMessages.userNamePlaceHolder,
                          })}
                          {...field}
                          onChange={event =>
                            onChange(event.target.value.replace(/\s/g, ''))
                          }
                          error={Boolean(errors.username?.message)}
                          helperText={errors.username?.message}
                          className={classes.formTextInput}
                          spellCheck={false}
                        />
                      </>
                    )}
                  />
                </FormControl>
                <FormControl variant="outlined" margin="normal" fullWidth>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field: { onChange, ...field } }) => (
                      <>
                        <label
                          className={classes.inputsLabel}
                          htmlFor="password-input"
                        >
                          {intl.formatMessage({
                            ...authMessages.passwordLabel,
                          })}
                        </label>
                        <TextField
                          id="password-input"
                          variant="outlined"
                          placeholder={intl.formatMessage({
                            ...authMessages.passwordPlaceHolder,
                          })}
                          onKeyDown={handleCapsLock}
                          onChange={event =>
                            onChange(event.target.value.replace(/\s/g, ''))
                          }
                          type={showPassword ? 'text' : 'password'}
                          onBlur={() => setIsCapsLockEnabled(false)}
                          error={Boolean(errors.password?.message)}
                          helperText={errors.password?.message}
                          className={classes.formTextInput}
                          spellCheck={false}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                style={{ position: 'absolute', right: 2 }}
                                position="end"
                              >
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  size="large"
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          {...field}
                        />
                      </>
                    )}
                  />
                </FormControl>
                {isCapsLockEnabled && (
                  <FormHelperText>CapsLock is enabled</FormHelperText>
                )}
                {errorMessage && (
                  <FormHelperText error>{errorMessage}</FormHelperText>
                )}
                <Typography
                  className={classes.hyperLink}
                  onClick={() => {
                    setForgetPassword(true);
                    setShowLogin(false);
                  }}
                >
                  Forgot your username/password?
                </Typography>
                <LoadableButton
                  transparent
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={isAuthenticatingUser}
                  isLoading={isAuthenticatingUser}
                >
                  <FormattedMessage {...authMessages.button} />
                </LoadableButton>
              </form>
            </>
          )}
          {showQrLogin && (
            <>
              <Box className={classes.titleBox}>
                <Typography
                  fontWeight={600}
                  fontSize="2em"
                  className={classes.descriptionColor}
                >
                  Log In with QR Code
                </Typography>
                <QRCode qrId={socketId} />
                <Typography
                  fontWeight={500}
                  className={classes.descriptionColor}
                >
                  1- Make sure you are logged into the Coligo LMS Mobile App.
                  Download App [
                  <a
                    className={classes.activeLink}
                    href="https://play.google.com/store/apps/details?id=com.coligotech.lms&hl=en"
                  >
                    android
                  </a>{' '}
                  -
                  <a
                    className={classes.activeLink}
                    href="https://apps.apple.com/eg/app/coligo-lms/id1484687171"
                  >
                    {' '}
                    ios
                  </a>
                  ]
                  <br />
                  2- Inside the app, tap &ldquo;Web Login&ldquo;.
                  <br />
                  3- Scan the QR code displayed on this page.
                  <br />
                  {/* Need to be re-visit to add original guide link */}
                  {/* 4- Need help?{' '}
                  <a
                    className={classes.activeLink}
                    href="https://www.google.com/"
                  >
                    [Watch this quick guide]
                  </a> */}
                </Typography>
              </Box>
            </>
          )}
          {forgetPassword && (
            <Request
              backToLogin={() => {
                setShowLogin(true);
                setForgetPassword(false);
                setShowPending(false);
                setShowChooseAccount(false);
              }}
              goToChooseAccount={data => {
                setShowChooseAccount(true);
                setForgetPassword(false);
                setShowLogin(false);
                setShowPending(false);
                setAccounts(data);
              }}
              goToPending={data => {
                setChosenAccount(data);
                setShowPending(true);
                setForgetPassword(false);
                setShowLogin(false);
                showChooseAccount(false);
              }}
            />
          )}
          {showChooseAccount && (
            <ChooseAccount
              backToRequest={() => {
                setShowLogin(false);
                setForgetPassword(true);
                setShowPending(false);
                setShowChooseAccount(false);
              }}
              goToPending={data => {
                setShowPending(true);
                setForgetPassword(false);
                setShowLogin(false);
                setShowChooseAccount(false);
                setChosenAccount(data);
              }}
              accounts={accounts}
            />
          )}
          {showPending && <Pending account={chosenAccount} />}
        </Box>

        <Box
          className={classes.qrButtonWrapper}
          onClick={() => {
            if (showLogin) {
              setShowQrLogin(true);
              setShowLogin(false);
              setForgetPassword(false);
            } else if (forgetPassword) {
              setForgetPassword(false);
              setShowLogin(true);
              setShowQrLogin(false);
            } else if (showQrLogin) {
              setShowQrLogin(false);
              setShowLogin(true);
              setForgetPassword(false);
            }
          }}
        >
          <Button
            transparent
            type="button"
            variant="contained"
            className={classes.qrLoginButton}
          >
            {showLogin && <QrCode2Icon className={classes.qrIconImg} />}
            <Typography className={classes.descriptionQrButton}>
              {showLogin ? 'Log In with QR Code' : 'Log In with username'}
            </Typography>
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
}

LoginForm.propTypes = {
  classes: PropTypes.shape().isRequired,
  login: PropTypes.func.isRequired,
  qrLogin: PropTypes.func.isRequired,
  errorMessage: PropTypes.string.isRequired,
  isAuthenticatingUser: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  errorMessage: state.auth.errorMessage,
  isAuthenticatingUser: state.auth.isAuthenticatingUser,
});
const mapDispatchToProps = dispatch => ({
  login: (formProps, callback, errorCallback, allRoles) =>
    dispatch(signIn(formProps, callback, errorCallback, allRoles)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles),
)(LoginForm);
